// assets/flat-lay-toggle.js
//
// Defines <flat-lay-toggle>, rendered by snippets/flat-lay-toggle.liquid
// inside snippets/filter-bar.liquid (desktop) and snippets/quick-filters.liquid
// (mobile).
//
// Swaps the product grid's images between on-model and flat-lay versions.
// Both URLs are already rendered into the page as data attributes on each
// product's .product-item__media wrapper (see the patch to
// snippets/product-item.liquid) — this is a pure client-side swap, no
// network requests, no page reload.
//
// SEMANTIC, spelled out explicitly since it's easy to invert by accident:
//   aria-pressed="true"  (toggle ON)  → "On Model" is active → show the
//                                       on-model photo (the page's default,
//                                       already-rendered src — no swap).
//   aria-pressed="false" (toggle OFF) → "On Model" is off    → show the
//                                       flat-lay photo instead.
// This matches both the button's own label ("On Model" — pressed/ON should
// mean that's what's showing) and how the original Constructor-powered
// version of this toggle behaved when we inspected it.
//
// IMPORTANT — matched to this theme's real markup (confirmed from the
// theme export), not a generic guess:
//   .product-item__media[data-flat-original-src]   <- the data lives here
//     └─ ...
//         └─ img.image__img                         <- the swap targets THIS
//
// image__img is the theme's shared image class used EVERYWHERE (hero
// banners, lookbooks, etc.), so we deliberately scope every lookup through
// the product-item__media wrapper rather than selecting img.image__img
// directly — selecting that class globally would touch images that have
// nothing to do with this toggle.

(() => {
  const STORAGE_KEY = "flatLayToggleState";
  const CONTAINER_SELECTOR = ".product-item__media";
  const IMAGE_SELECTOR = "img.image__img";

  // Returns whether "On Model" should be ON (true) or OFF (false).
  // defaultValue is what to fall back to when there's no stored preference
  // yet — comes from the "Default to On Model" section setting via the
  // data-default-on-model attribute rendered on this element, so a merchant
  // can flip the out-of-the-box default from the theme customizer without
  // touching code.
  function getStoredOnModelState(defaultValue) {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored === "flat") return false;
      if (stored === "model") return true;
      // No stored preference yet (first visit this session): fall back to
      // whatever the section setting says. Note this can differ from what
      // the page's HTML rendered by default (product-item.liquid always
      // renders the on-model photo as the base src) — if the merchant sets
      // the default to flat, connectedCallback below still needs to swap
      // on load to match, same as any other non-default state.
      return defaultValue;
    } catch {
      return defaultValue; // sessionStorage unavailable (private browsing, etc.)
    }
  }

  function setStoredOnModelState(isOnModel) {
    try {
      sessionStorage.setItem(STORAGE_KEY, isOnModel ? "model" : "flat");
    } catch {
      // ignore — worst case the toggle just doesn't persist across pages
    }
  }

  // isFlat: true means "show the flat-lay photo for this container".
  function applyStateToContainer(container, isFlat) {
    const flatSrc = container.getAttribute("data-flat-original-src");
    if (!flatSrc) return; // this product has no flat-lay alternate — leave it alone

    const img = container.querySelector(IMAGE_SELECTOR);
    if (!img) return;

    const flatSrcset = container.getAttribute("data-flat-original-srcset");

    if (isFlat && !img.dataset.flatApplied) {
      img.setAttribute("data-model-original-src", img.getAttribute("src"));
      img.setAttribute("data-model-original-srcset", img.getAttribute("srcset") || "");
      img.setAttribute("src", flatSrc);
      if (flatSrcset) img.setAttribute("srcset", flatSrcset);
      img.dataset.flatApplied = "true";
    } else if (!isFlat && img.dataset.flatApplied) {
      const modelSrc = img.getAttribute("data-model-original-src");
      const modelSrcset = img.getAttribute("data-model-original-srcset");
      if (modelSrc) img.setAttribute("src", modelSrc);
      if (modelSrcset) img.setAttribute("srcset", modelSrcset);
      delete img.dataset.flatApplied;
    }
  }

  function applyStateToAll(isFlat) {
    document
      .querySelectorAll(`${CONTAINER_SELECTOR}[data-flat-original-src]`)
      .forEach((container) => applyStateToContainer(container, isFlat));
  }

  class FlatLayToggle extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector(".flat-toggle__switch");
      if (!this.button) return;

      // Comes from the "Default to On Model" section setting, rendered as
      // this attribute by flat-lay-toggle.liquid. Note: the page's raw HTML
      // (product-item.liquid) always renders the on-model photo as the base
      // src regardless of this setting, so if the merchant has set the
      // default to flat, there's a brief flash of on-model before this
      // swaps it on load — same one-time cost as any other non-default
      // state change, just now possibly happening on first load too.
      const sectionDefault = this.getAttribute("data-default-on-model") !== "false";

      const isOnModel = getStoredOnModelState(sectionDefault);
      this.button.setAttribute("aria-pressed", String(isOnModel));
      applyStateToAll(!isOnModel); // isFlat = the opposite of On Model being on

      // type="button" on the button itself already prevents this from
      // submitting the surrounding <form class="filter-bar">, but stop
      // propagation too in case the theme has a delegated form-submit
      // listener that isn't type-gated.
      this.button.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggle();
      });

      // Infinite-scroll pagination and filter re-renders both inject new
      // product-item markup after page load — re-apply the current state
      // to anything that shows up later.
      this._observer = new MutationObserver((mutations) => {
        const currentIsOnModel = this.button.getAttribute("aria-pressed") === "true";
        for (const mutation of mutations) {
          mutation.addedNodes.forEach((node) => {
            if (!(node instanceof Element)) return;
            if (node.matches?.(CONTAINER_SELECTOR)) {
              applyStateToContainer(node, !currentIsOnModel);
            }
            node.querySelectorAll?.(CONTAINER_SELECTOR).forEach((el) =>
              applyStateToContainer(el, !currentIsOnModel)
            );
          });
        }
      });
      this._observer.observe(document.body, { childList: true, subtree: true });
    }

    disconnectedCallback() {
      this._observer?.disconnect();
    }

    toggle() {
      const isOnModel = this.button.getAttribute("aria-pressed") !== "true";
      // Two instances of this element exist in the page at once — one inside
      // filter-bar (desktop, ≥720px) and one inside quick-filters (mobile,
      // <720px), with CSS media queries deciding which is visible. Keep
      // both buttons' visual state in sync so a resize never shows a stale
      // position on the one that was hidden when the user clicked the other.
      document.querySelectorAll(".flat-toggle__switch").forEach((btn) => {
        btn.setAttribute("aria-pressed", String(isOnModel));
      });
      setStoredOnModelState(isOnModel);
      applyStateToAll(!isOnModel);
    }
  }

  customElements.define("flat-lay-toggle", FlatLayToggle);
})();
